import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowIcon } from "@/pages/city/related-cities/icons";

interface NavButtonProps extends React.ComponentProps<typeof Button> {
  iconClassName?: string;
}

function NavButton({ className, iconClassName, ...props }: NavButtonProps) {
  return (
    <Button
      className={cn(
        "absolute top-1/2 z-1 hidden size-12.5 -translate-y-1/2 rounded-full bg-white text-ruby-red transition-[colors,opacity,visibility] md:flex",
        "hover:bg-ruby-red hover:text-white",
        className
      )}
      {...props}
    >
      <ArrowIcon className={cn("text-inherit", iconClassName)} />
    </Button>
  );
}

interface CarouselProps {
  ariaLabels: string[];
  children?: React.ReactNode;
  className?: string;
  dataLength: number;
}

function Carousel({
  ariaLabels,
  children,
  className,
  dataLength,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reindex on children change
  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectingEntry = entries.find((entry) => entry.isIntersecting);
        if (intersectingEntry) {
          setCurrentIndex(
            Number.parseInt(
              intersectingEntry.target.getAttribute("data-index") || "0",
              10
            )
          );
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 1,
      }
    );

    scrollContainerRef.current.childNodes.forEach((child, index) => {
      (child as HTMLElement).setAttribute("data-index", index.toString());
      observer.observe(child as HTMLElement);
    });

    return () => observer.disconnect();
  }, [children]);

  const onSwipedLeft = () => {
    const nextIndex = Math.min(currentIndex + 3, dataLength - 1);
    onSetCurrentIndex(nextIndex, "start");
  };

  const onSwipedRight = () => {
    const nextIndex = Math.max(currentIndex - 3, 0);
    onSetCurrentIndex(nextIndex, "end");
  };

  const onSetCurrentIndex = (
    index: number,
    inline: ScrollLogicalPosition = "center"
  ) => {
    (
      scrollContainerRef.current?.childNodes[index] as HTMLElement
    )?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline,
    });
  };

  return (
    <div className={cn(className)}>
      <div className="relative [--half-width:calc(min(var(--container-7xl),100vw)/2)]">
        <NavButton
          aria-label="Previous"
          className={cn(
            "left-1/2 -translate-x-[min(calc(var(--half-width)+3.125rem),50vw)]",
            currentIndex > 0 ? "md:visible" : "md:invisible"
          )}
          onClick={onSwipedRight}
        />
        <div
          className="scrollbar-hidden flex w-full snap-x snap-mandatory items-stretch gap-[25px] overflow-x-auto px-6 pt-[3px] *:snap-center md:px-[calc((50vw-var(--half-width)+1.5rem))] md:pt-0"
          ref={scrollContainerRef}
        >
          {children}
        </div>
        <NavButton
          aria-label="Next"
          className={cn(
            "right-1/2 translate-x-[min(calc(var(--half-width)+3.125rem),50vw)]",
            currentIndex < dataLength - 1 ? "visible" : "invisible"
          )}
          iconClassName="rotate-180"
          onClick={onSwipedLeft}
        />
      </div>
      <div className="mt-5 flex justify-center gap-2 md:hidden">
        {ariaLabels.map((ariaLabel: string, index: number) => (
          <Button
            aria-label={ariaLabel}
            className={cn(
              "size-2 rounded-full transition-[colors,shadow]",
              index === currentIndex
                ? "bg-rose-red shadow-0-3-5 shadow-dark-magenta/64"
                : "border border-white bg-transparent"
            )}
            key={ariaLabel}
            onClick={() => onSetCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}

export default Carousel;
